import { useState, type ReactNode } from 'react';
import { OVERLAY_ATTRIBUTE } from '../board-grid/useZoomPan';
import { useBoxSize } from '../use-box-size';

/** How far the panel stands off the mark it belongs to, on screen. */
const GAP_PX = 12;

/** How near the picture's edge the panel may come. */
const EDGE_PX = 8;

/**
 * Over everything, the navbar included.
 *
 * A hold circled near the top of the picture opens its panel *upwards*, which
 * puts it exactly where the navbar is once the page has been scrolled at all —
 * and being half-covered by the app's own chrome is worse than useless on the
 * one control the setter is using. `z-50` clears the navbar and the save button,
 * both `z-40`.
 *
 * This works because nothing between here and the page root makes a stacking
 * context of its own — no `z-index`, no `transform`, no `filter` on the blocks
 * the editor is built from — so this number is compared against the navbar's
 * directly. Putting a `z-*` or a transform on one of those wrappers would trap
 * the panel underneath it again, and the fix then is a portal, not a bigger
 * number.
 *
 * Dialogs are `z-50` too but are portalled to the end of the body, so they still
 * open over this — which is what the legend behind the (i) wants.
 */
const ABOVE_EVERYTHING = 'z-50';

interface AnchoredMarkPopoverProps {
  /** The mark this acts on, in fractions (0-1) of the content it is drawn on. */
  at: { x: number; y: number };
  /**
   * How far out of the mark's centre the panel has to stand to clear it, as a
   * fraction of the picture's **width** — which is the unit both a circle on a
   * photo and a hold on a board are already measured in, both being round.
   */
  radius?: number;
  /** Where the content sits in the frame right now — see `useZoomPan`'s `view`. */
  view: { scale: number; x: number; y: number };
  /**
   * The picture this is anchored over. Measured rather than asked for: the panel
   * is kept inside it, and which side of the mark it opens on is whichever side
   * has the room.
   */
  frame: HTMLElement | null;
  testId?: string;
  children: ReactNode;
}

/**
 * A panel that hangs off a mark on the picture — the circle in hand, the point
 * in hand — instead of across the foot of the screen.
 *
 * At the mark because that is where the setter is looking: a bar at the bottom
 * makes them read the wall, look away to answer what the hold is, and find their
 * place again for the next one. It costs a row of the screen while it is up, and
 * on a phone held at the wall the answer ends up as far from the question as the
 * screen allows.
 *
 * It sits *beside* the transformed layer rather than in it, so the picture can
 * be zoomed to place a hold precisely without the panel swelling with it. That
 * is what `view` is for: the anchor is a fraction of the content, and this turns
 * it back into a place in the frame.
 *
 * It opens below the mark and flips above it when there is no room, and it never
 * comes closer than `EDGE_PX` to either edge — a hold circled at the side of the
 * wall would otherwise hang half of the panel off the screen.
 */
export function AnchoredMarkPopover({
  at,
  radius = 0,
  view,
  frame,
  testId,
  children,
}: AnchoredMarkPopoverProps) {
  const box = useBoxSize(frame);
  // The panel measures itself, because where it can go depends on how big it is
  // — and it changes size when it is expanded.
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  const panelBox = useBoxSize(panel);

  const anchorX = at.x * view.scale * box.width + view.x;
  const anchorY = at.y * view.scale * box.height + view.y;
  const offset = radius * view.scale * box.width;

  // Below by default: the mark is what was just placed, and covering the wall
  // under it hides less than covering the wall above it, which is where the
  // climb carries on.
  const above =
    box.height > 0 && anchorY + offset + GAP_PX + panelBox.height > box.height - EDGE_PX;

  return (
    <div
      className={`pointer-events-none absolute inset-0 ${ABOVE_EVERYTHING}`}
      // Sat on the picture rather than in it: the gesture that dragged the photo
      // has no claim on a press that lands here.
      {...{ [OVERLAY_ATTRIBUTE]: '' }}
    >
      <div
        ref={setPanel}
        data-testid={testId}
        className="pointer-events-auto absolute w-max max-w-full"
        style={{
          left: centred(anchorX, panelBox.width, box.width),
          top: above ? anchorY - offset - GAP_PX : anchorY + offset + GAP_PX,
          transform: `translate(-50%, ${above ? '-100%' : '0'})`,
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Where the panel's centre goes: over the mark, pulled back inside the picture
 * when that would hang it off an edge — and in the middle when the picture is
 * narrower than the panel, since then there is no edge to prefer.
 */
function centred(anchor: number, panelWidth: number, frameWidth: number): number {
  if (frameWidth <= 0) return anchor;

  const room = frameWidth - panelWidth - EDGE_PX * 2;
  if (room <= 0) return frameWidth / 2;

  return Math.min(Math.max(anchor, panelWidth / 2 + EDGE_PX), frameWidth - panelWidth / 2 - EDGE_PX);
}
